import { graphqlRequest } from "./client";
import { downloadExport, viewFile, type ViewedFile } from "./admin";
import type { BoardTrack, Track } from "./types";
import type { WinPaid } from "./gigboard";

/* Admin side of the Gig Board: job sources, partner feeds, direct posts, reported wins, reminders and insights. */

export interface GigSource {
  id: string;
  name: string;
  site: string;
  kind: string;
  note: string;
  /** Settings the source needs that aren't set on the server (it stays off until they are). */
  missing: string[];
  enabled: boolean;
  lastRun?: string | null;
  lastOk?: string | null;
  live: number;
  error?: string | null;
}

export interface PartnerFeed {
  name: string;
  url: string;
  track: BoardTrack;
  openToNigeria: boolean;
}

export interface DirectPost {
  title: string;
  url: string;
  track: BoardTrack;
  company: string;
  pay: string;
  expires?: string | null;
  desc: string;
  posted?: string;
}

export interface GigSourcesView {
  sources: GigSource[];
  feeds: PartnerFeed[];
  posts: DirectPost[];
  refreshing: boolean;
  totalListings: number;
}

export interface WinAdminRow {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  participantId?: string | null;
  email: string;
  track?: Track | null;
  what: string;
  type: string;
  via: string;
  startDate?: string | null;
  paid: WinPaid;
  amount?: number | null;
  currency: string;
  hasProof: boolean;
  reportedAt: string;
  checkedAt?: string | null;
}

export interface WinsDashboard {
  youthInWork: number;
  paidWins: number;
  checked: number;
  total: number;
  rows: WinAdminRow[];
}

export interface PushRound {
  id: string;
  roundId: string;
  kind: string;
  started: string;
  finished?: string | null;
  total: number;
  sent: number;
  failed: number;
}

export interface RemindersStatus {
  enabled: boolean;
  reason?: string | null;
  devices: number;
  people: number;
  schedule: string;
  rounds: PushRound[];
}

export interface InsightsRow {
  email: string;
  participantId?: string | null;
  track?: Track | null;
  /** When gig steps 1 to 5 were done (ISO), or "" when not yet. */
  stepAt: string[];
  youthInWork: boolean;
  bidReadiness?: number | null;
  /** Null when the viewer can't see finance. */
  financeReferred?: boolean | null;
  joinedAt: string;
}

export const WINS_PAGE_SIZE = 50;

const SOURCES_FIELDS = `refreshing totalListings
  sources { id name site kind note missing enabled lastRun lastOk live error }
  feeds { name url track openToNigeria }
  posts { title url track company pay expires desc posted }`;

const WIN_ROW_FIELDS =
  "id userId firstName lastName participantId email track what type via startDate paid amount currency hasProof reportedAt checkedAt";

export async function fetchGigSources(): Promise<GigSourcesView> {
  return (
    await graphqlRequest<{ getGigSources: GigSourcesView }>(
      `{ getGigSources { ${SOURCES_FIELDS} } }`,
    )
  ).getGigSources;
}

/** Refresh one source, or all of them; runs in the background on the server. */
export async function refreshGigSources(sourceId?: string): Promise<boolean> {
  return (
    await graphqlRequest<{ refreshGigSources: boolean }>(
      `mutation($sourceId: String) { refreshGigSources(sourceId: $sourceId) }`,
      { variables: { sourceId: sourceId ?? null } },
    )
  ).refreshGigSources;
}

export async function saveGigFeeds(feeds: PartnerFeed[]): Promise<GigSourcesView> {
  return (
    await graphqlRequest<{ saveGigFeeds: GigSourcesView }>(
      `mutation($feeds: [PartnerFeedInput!]!) { saveGigFeeds(feeds: $feeds) { ${SOURCES_FIELDS} } }`,
      { variables: { feeds } },
    )
  ).saveGigFeeds;
}

export async function saveGigPosts(posts: DirectPost[]): Promise<GigSourcesView> {
  const input = posts.map(({ title, url, track, company, pay, expires, desc }) => ({
    title,
    url,
    track,
    company,
    pay,
    expires: expires || null,
    desc,
  }));
  return (
    await graphqlRequest<{ saveGigPosts: GigSourcesView }>(
      `mutation($posts: [DirectPostInput!]!) { saveGigPosts(posts: $posts) { ${SOURCES_FIELDS} } }`,
      { variables: { posts: input } },
    )
  ).saveGigPosts;
}

export async function fetchWins(filter: {
  search?: string;
  pageNumber: number;
  limit?: number;
}): Promise<WinsDashboard> {
  return (
    await graphqlRequest<{ getWins: WinsDashboard }>(
      `query($filter: WinsFilterInput!) { getWins(filter: $filter) { youthInWork paidWins checked total rows { ${WIN_ROW_FIELDS} } } }`,
      { variables: { filter: { limit: WINS_PAGE_SIZE, ...filter } } },
    )
  ).getWins;
}

/** MERL only: mark a win checked against its proof. */
export async function setWinChecked(winId: string, checked: boolean): Promise<WinAdminRow> {
  return (
    await graphqlRequest<{ setWinChecked: WinAdminRow }>(
      `mutation($winId: String!, $checked: Boolean!) { setWinChecked(winId: $winId, checked: $checked) { ${WIN_ROW_FIELDS} } }`,
      { variables: { winId, checked } },
    )
  ).setWinChecked;
}

export function viewWinProof(winId: string): Promise<ViewedFile> {
  return viewFile(`/api/admin/view/win-proof/${encodeURIComponent(winId)}`);
}

export function downloadWins(): Promise<void> {
  return downloadExport("wins");
}

export async function fetchRemindersStatus(): Promise<RemindersStatus> {
  return (
    await graphqlRequest<{ getRemindersStatus: RemindersStatus }>(
      `{ getRemindersStatus { enabled reason devices people schedule rounds { id roundId kind started finished total sent failed } } }`,
    )
  ).getRemindersStatus;
}

/** Send a test reminder to everyone signed in with this email who has reminders on. */
export async function testReminder(
  email: string,
): Promise<{ devices: number; sent: number; failed: number }> {
  return (
    await graphqlRequest<{ testReminder: { devices: number; sent: number; failed: number } }>(
      `mutation($email: String!) { testReminder(email: $email) { devices sent failed } }`,
      { variables: { email } },
    )
  ).testReminder;
}

export async function fetchInsightsData(): Promise<{ total: number; rows: InsightsRow[] }> {
  return (
    await graphqlRequest<{ getInsightsData: { total: number; rows: InsightsRow[] } }>(
      `{ getInsightsData { total rows { email participantId track stepAt youthInWork bidReadiness financeReferred joinedAt } } }`,
    )
  ).getInsightsData;
}
